// IP合作二级单「音乐权益」字段契约与分组配置。
// 字段 key 已对齐接口 IpCooperationMusicBenefitDto（嵌套结构：authorized_music.music_name 等），
// 枚举/下拉选项全部引用 api.ts 生成 enums（禁止前端自建一套）。
// 外层结构：歌曲 Tab + Form.List name="music_benefit"（IpCooperationMusicBenefitDto[]，每首歌一个元素），
// 本文件定义每首歌内部分组与字段（namePath = 歌曲对象内的嵌套 key 数组，MusicBenefitTable 拼接 [songIndex, ...key]）。
// 长文本字段统一 maxLength=800（PRD：所有长文本 800 字以内）。

import { enums } from '#services/api';
import type { SFormComponentClass } from '@mpc/common/src/components/SForm/SFormElement';

/** 附加IP类型 = 音乐：音乐权益模块显隐开关值（radio extra_ip_type） */
export const MUSIC_ADDITIONAL_IP_TYPE =
  enums.IpCooperationExtraIpTypeEnum.MUSIC;

/**
 * 音乐权益 NEGOTIATION 区块标识（对齐后端 IpCooperationNegotiationItemVo.section = 'MUSIC_BENEFIT'）。
 * 内包回显（itemsByKey 按 section 过滤）与外包提交（ItemResult.item_key）共用此常量，避免前端散落硬编码。
 */
export const MUSIC_BENEFIT_SECTION = 'MUSIC_BENEFIT';
/** 音乐权益 NEGOTIATION 条款 key 前缀（后端约定 item_key = music_benefit_0/music_benefit_1/...，按歌曲 index） */
export const MUSIC_BENEFIT_ITEM_KEY_PREFIX = 'music_benefit_';
/** 单首歌 NEGOTIATION 条款 key：music_benefit_{songIndex}（对齐 ItemResult / IpCooperationNegotiationItemVo 约定） */
export const getMusicBenefitItemKey = (songIndex: number | string): string =>
  `${MUSIC_BENEFIT_ITEM_KEY_PREFIX}${songIndex}`;

/** 需求使用-使用范围是否包含产品端（scope ∈ {ONLY_FOR_PRODUCT, MARKETING_AND_PRODUCT}） */
export function musicScopeHasProduct(scope?: string): boolean {
  return (
    scope === enums.IpCooperationMusicUsageScopeEnum.ONLY_FOR_PRODUCT ||
    scope === enums.IpCooperationMusicUsageScopeEnum.MARKETING_AND_PRODUCT
  );
}

/** 需求使用-使用范围是否包含营销端（scope ∈ {ONLY_FOR_MARKETING, MARKETING_AND_PRODUCT}） */
export function musicScopeHasMarketing(scope?: string): boolean {
  return (
    scope === enums.IpCooperationMusicUsageScopeEnum.ONLY_FOR_MARKETING ||
    scope === enums.IpCooperationMusicUsageScopeEnum.MARKETING_AND_PRODUCT
  );
}

/** Apollo 发行区域选项 key（country_region_options）：素材所需授权区域选项的名称翻译来源（镜像 ProductInfoTable 联动所属区域列） */
export const MUSIC_REGION_OPTION_MAP_KEY = 'country_region_options';
/**
 * 素材所需授权区域选项 key：范围由 MusicBenefitTable 取产品列表已选的「联动所属区域」
 * （各行 linked_release_region 合并去重）计算后经 optionMap 注入（随产品列表联动增删，非 Apollo 全量枚举）。
 */
export const MUSIC_MATERIAL_AUTH_REGION_OPTION_KEY =
  'material_auth_region_options';
/** 音乐上架平台-产品 选项来源：上架平台选项经 optionMap 动态注入 */
export const MUSIC_PLATFORM_PRODUCT_OPTION_MAP_KEY = 'launch_platform_options';

/**
 * 单个字段定义（歌曲对象内 namePath）。
 * elementProps/required/show 可为函数，SFormItem 会以 renderData（含 formData=当前歌曲对象 + optionMap）求值。
 */
export interface MusicBenefitField {
  /** 歌曲对象内嵌套 namePath（不含 songIndex 前缀），如 ['authorized_music', 'music_name'] */
  key: string[];
  label: string;
  componentClass?: SFormComponentClass;
  /** 函数式（renderData）=> options 等；静态对象直接传 */
  elementProps?: any | ((renderData: any) => any);
  /** 函数式（renderData）=> boolean 或静态 */
  required?: boolean | ((renderData: any) => boolean);
  /** 函数式（renderData）=> boolean（默认 true）或静态 */
  show?: boolean | ((renderData: any) => boolean);
  /** 提示文案 */
  tip?: string;
}

/** 音乐权益分组（行 = 权益类别；fields = 组内字段） */
export interface MusicBenefitGroup {
  group_key: string;
  group_name: string;
  fields: MusicBenefitField[];
}

// renderData.formData = 当前歌曲对象（MusicBenefitTable 注入），供 show/required 联动谓词读取
const song = (renderData: any) => renderData?.formData ?? {};
// 嵌套字段读取辅助：授权音乐详情（authorized_music_detail.*）与音乐使用形式（music_usage_form.*）
const musicDetail = (renderData: any) =>
  song(renderData)?.authorized_music_detail ?? {};
const usageForm = (renderData: any) =>
  song(renderData)?.music_usage_form ?? {};
const usageScope = (renderData: any) => song(renderData)?.usage_scope;
const hasProduct = (renderData: any) =>
  musicScopeHasProduct(usageScope(renderData));
const hasMarketing = (renderData: any) =>
  musicScopeHasMarketing(usageScope(renderData));

/** 是/否 枚举选项（CommonYesNoEnum 0=否 1=是）—— 反转顺序为「是/否」展示（对齐走查：radio 先「是」后「否」） */
const yesNoOptions = enums.CommonYesNoEnum.values().slice().reverse();

/**
 * 素材所需授权区域只读态展示：已选区域 code→中文名（Apollo 发行区域选项翻译，未知 code 保留原值；
 * 镜像 ProductInfoTable「联动所属区域」列 showComponent），历史单据存有超出联动范围的值时不至于展示为空。
 */
const renderMaterialAuthRegionNames = (
  renderData: any,
  value?: string[]
): string => {
  const regionNameMap: Record<string, string> = {};
  (renderData?.optionMap?.[MUSIC_REGION_OPTION_MAP_KEY] || []).forEach(
    (it: any) => {
      if (it?.value != null) regionNameMap[it.value] = it?.name ?? it.value;
    }
  );
  const text = (Array.isArray(value) ? value : [])
    .map((code) => regionNameMap[code] ?? code)
    .join('，');
  return text || '--';
};

/** 长文本字段公共 props（maxLength 800 + 计数 + 2 行） */
const LONG_TEXT_PROPS = {
  placeholder: '请填写',
  maxLength: 800,
  showCount: true,
  rows: 2,
};

export const MUSIC_BENEFIT_GROUPS: MusicBenefitGroup[] = [
  {
    group_key: 'usage',
    group_name: '需求使用',
    fields: [
      {
        key: ['usage_scope'],
        label: '使用范围',
        componentClass: 'select',
        required: true,
        elementProps: {
          options: enums.IpCooperationMusicUsageScopeEnum.values(),
          placeholder: '请选择',
          showSearch: true,
        },
      },
    ],
  },
  {
    group_key: 'music_required',
    group_name: '需要授权的音乐',
    fields: [
      {
        key: ['authorized_music', 'music_name'],
        label: '音乐名称',
        componentClass: 'textarea',
        required: true,
        elementProps: LONG_TEXT_PROPS,
      },
      {
        key: ['authorized_music', 'music_link'],
        label: '音乐链接',
        componentClass: 'textarea',
        elementProps: LONG_TEXT_PROPS,
      },
    ],
  },
  {
    group_key: 'music_detail',
    group_name: '授权音乐详情&数量',
    fields: [
      {
        key: ['authorized_music_detail', 'only_lyrics_authorization'],
        label: '是否仅需要词曲授权',
        componentClass: 'radio',
        required: true,
        elementProps: { options: yesNoOptions },
      },
      {
        key: ['authorized_music_detail', 'other_authorization_content'],
        label: '其他授权内容',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          musicDetail(rd)?.only_lyrics_authorization ===
          enums.CommonYesNoEnum.NO,
        elementProps: LONG_TEXT_PROPS,
      },
      {
        key: ['authorized_music_detail', 'need_original_sing_authorization'],
        label: '是否需要原唱权益授权',
        componentClass: 'radio',
        required: true,
        elementProps: { options: yesNoOptions },
      },
      {
        key: ['authorized_music_detail', 'involve_lyrics_adaptation'],
        label: '是否涉及词曲改编',
        componentClass: 'radio',
        required: true,
        elementProps: { options: yesNoOptions },
      },
      {
        key: ['authorized_music_detail', 'lyrics_adaptation_count'],
        label: '词曲授权改编数量',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          musicDetail(rd)?.involve_lyrics_adaptation ===
          enums.CommonYesNoEnum.YES,
        elementProps: LONG_TEXT_PROPS,
      },
      {
        key: ['authorized_music_detail', 'lyrics_adaptation_duration'],
        label: '词曲授权改编时长',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          musicDetail(rd)?.involve_lyrics_adaptation ===
          enums.CommonYesNoEnum.YES,
        elementProps: LONG_TEXT_PROPS,
      },
      {
        key: ['authorized_music_detail', 'adaptation_detail_list'],
        label: '如涉及多个改编请附上改编详情列表',
        componentClass: 'textarea',
        show: (rd) =>
          musicDetail(rd)?.involve_lyrics_adaptation ===
          enums.CommonYesNoEnum.YES,
        elementProps: LONG_TEXT_PROPS,
      },
      {
        key: ['authorized_music_detail', 'will_cover_song'],
        label: '是否会对歌曲进行翻唱',
        componentClass: 'radio',
        required: true,
        elementProps: { options: yesNoOptions },
      },
      {
        key: ['authorized_music_detail', 'cover_count'],
        label: '翻唱数量',
        componentClass: 'inputNumber',
        required: true,
        show: (rd) =>
          musicDetail(rd)?.will_cover_song === enums.CommonYesNoEnum.YES,
        elementProps: {
          placeholder: '请输入',
          min: 0,
          precision: 0,
          toFixed: 0,
        },
      },
      {
        key: ['authorized_music_detail', 'cover_singer'],
        label: '翻唱者',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          musicDetail(rd)?.will_cover_song === enums.CommonYesNoEnum.YES,
        elementProps: LONG_TEXT_PROPS,
      },
    ],
  },
  {
    group_key: 'usage_form',
    group_name: '音乐使用形式',
    fields: [
      {
        key: ['music_usage_form', 'product_usage_form'],
        label: '产品端',
        componentClass: 'select',
        show: hasProduct,
        elementProps: {
          options: enums.IpCooperationMusicProductUsageFormEnum.values(),
          placeholder: '请选择',
          showSearch: true,
        },
      },
      {
        key: ['music_usage_form', 'marketing_usage_form'],
        label: '营销端',
        componentClass: 'select',
        show: hasMarketing,
        elementProps: {
          options: enums.IpCooperationMusicMarketingUsageFormEnum.values(),
          placeholder: '请选择',
          showSearch: true,
        },
      },
      {
        key: ['music_usage_form', 'promo_video_count'],
        label: '宣传视频数量',
        componentClass: 'inputNumber',
        required: true,
        show: (rd) =>
          hasMarketing(rd) &&
          usageForm(rd)?.marketing_usage_form ===
            enums.IpCooperationMusicMarketingUsageFormEnum.PROMO_VIDEO_BGM,
        elementProps: {
          placeholder: '请输入',
          min: 0,
          precision: 0,
          toFixed: 0,
        },
      },
      {
        key: ['music_usage_form', 'offline_placement_content'],
        label: '线下投放内容',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          hasMarketing(rd) &&
          usageForm(rd)?.marketing_usage_form ===
            enums.IpCooperationMusicMarketingUsageFormEnum.OFFLINE_PLACEMENT,
        elementProps: LONG_TEXT_PROPS,
      },
      {
        key: ['music_usage_form', 'marketing_other_content'],
        label: '营销其他内容',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          hasMarketing(rd) &&
          usageForm(rd)?.marketing_usage_form ===
            enums.IpCooperationMusicMarketingUsageFormEnum.OTHER,
        elementProps: LONG_TEXT_PROPS,
      },
    ],
  },
  {
    group_key: 'usage_duration',
    group_name: '音乐使用时长',
    fields: [
      {
        key: ['music_usage_duration', 'usage_duration'],
        label: '音乐使用时长',
        componentClass: 'textarea',
        required: true,
        // 提示文案作为输入框 placeholder（去掉 tip 浮层提示）
        elementProps: {
          ...LONG_TEXT_PROPS,
          placeholder:
            '周年庆前瞻：预计会使用10秒片段，用于预告宣发；联动PV：预计会使用90秒以内的原曲，用于联动宣发；端内：预计会使用一支完整的原曲，用于端内背景音乐BGM',
        },
      },
    ],
  },
  {
    group_key: 'bg_tone',
    group_name: '主体音/背景音',
    fields: [
      {
        key: ['main_bgm', 'main_bgm'],
        label: '主体音/背景音',
        componentClass: 'textarea',
        // 提示文案作为输入框 placeholder（去掉 tip 浮层提示）
        elementProps: {
          ...LONG_TEXT_PROPS,
          placeholder: '周年庆前瞻和联动PV使用主体音；端内可能会二者选其一',
        },
      },
    ],
  },
  {
    group_key: 'material_period',
    group_name: '素材制作周期',
    fields: [
      {
        key: ['material_production_cycle', 'production_cycle'],
        label: '素材制作周期',
        componentClass: 'textarea',
        // 提示文案作为输入框 placeholder（去掉 tip 浮层提示）
        elementProps: {
          ...LONG_TEXT_PROPS,
          placeholder: '周年庆前瞻预计11月完稿；PV及端内预计12月完稿',
        },
      },
    ],
  },
  {
    group_key: 'region',
    group_name: '素材所需授权区域',
    fields: [
      {
        key: ['material_auth_region', 'product'],
        label: '产品',
        componentClass: 'select',
        show: hasProduct,
        elementProps: (rd: any) => ({
          options: rd?.optionMap?.[MUSIC_MATERIAL_AUTH_REGION_OPTION_KEY] || [],
          multiple: true,
          placeholder: '请选择',
          showSearch: true,
          showComponent: (value: any) =>
            renderMaterialAuthRegionNames(rd, value),
        }),
      },
      {
        key: ['material_auth_region', 'marketing'],
        label: '营销',
        componentClass: 'select',
        show: hasMarketing,
        elementProps: (rd: any) => ({
          options: rd?.optionMap?.[MUSIC_MATERIAL_AUTH_REGION_OPTION_KEY] || [],
          multiple: true,
          placeholder: '请选择',
          showSearch: true,
          showComponent: (value: any) =>
            renderMaterialAuthRegionNames(rd, value),
        }),
      },
    ],
  },
  {
    group_key: 'auth_time',
    group_name: '授权时间',
    fields: [
      {
        key: ['auth_time', 'product'],
        label: '产品',
        componentClass: 'select',
        show: hasProduct,
        elementProps: {
          options: enums.IpCooperationMusicAuthTimeEnum.values(),
          placeholder: '请选择',
          showSearch: true,
        },
      },
      {
        key: ['auth_time', 'product_auth_deadline_desc'],
        label: '产品端授权截止时间说明',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          hasProduct(rd) &&
          song(rd)?.auth_time?.product ===
            enums.IpCooperationMusicAuthTimeEnum.NON_PERMANENT,
        elementProps: LONG_TEXT_PROPS,
      },
      {
        key: ['auth_time', 'marketing'],
        label: '营销',
        componentClass: 'select',
        show: hasMarketing,
        elementProps: {
          options: enums.IpCooperationMusicAuthTimeEnum.values(),
          placeholder: '请选择',
          showSearch: true,
        },
      },
      {
        key: ['auth_time', 'marketing_auth_deadline_desc'],
        label: '营销端授权截止时间说明',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          hasMarketing(rd) &&
          song(rd)?.auth_time?.marketing ===
            enums.IpCooperationMusicAuthTimeEnum.NON_PERMANENT,
        elementProps: LONG_TEXT_PROPS,
      },
    ],
  },
  {
    group_key: 'platform',
    group_name: '音乐上架平台',
    fields: [
      {
        key: ['music_platform', 'product'],
        label: '产品',
        componentClass: 'select',
        show: hasProduct,
        elementProps: (rd: any) => ({
          options: rd?.optionMap?.[MUSIC_PLATFORM_PRODUCT_OPTION_MAP_KEY] || [],
          multiple: true,
          placeholder: '请选择',
          showSearch: true,
        }),
      },
      {
        key: ['music_platform', 'marketing'],
        label: '营销',
        componentClass: 'select',
        show: hasMarketing,
        elementProps: {
          options: enums.IpCooperationMusicMarketingPlatformEnum.values(),
          multiple: true,
          placeholder: '请选择',
          showSearch: true,
        },
      },
      {
        key: ['music_platform', 'other_platform'],
        label: '其他平台',
        componentClass: 'textarea',
        required: true,
        show: (rd) =>
          hasMarketing(rd) &&
          Array.isArray(song(rd)?.music_platform?.marketing) &&
          song(rd)?.music_platform?.marketing.includes(
            enums.IpCooperationMusicMarketingPlatformEnum.OTHER
          ),
        elementProps: LONG_TEXT_PROPS,
      },
    ],
  },
  {
    group_key: 'material_deadline',
    group_name: '素材需要到位时间',
    fields: [
      {
        key: ['material_deadline', 'deadline'],
        label: '截止',
        componentClass: 'textarea',
        required: true,
        elementProps: LONG_TEXT_PROPS,
      },
    ],
  },
  {
    group_key: 'remark',
    group_name: '备注/补充',
    fields: [
      {
        // 备注/补充：后端 IpCooperationMusicBenefitDTO.remark 为 String（PRD：长文本，非必填），单层 namePath ['remark']
        key: ['remark'],
        label: '备注/补充',
        componentClass: 'textarea',
        elementProps: LONG_TEXT_PROPS,
      },
    ],
  },
];
